/**
 * What a licence's activity journal records (#566): these six, and only these.
 *
 *  - `user_added`       a NON-admin user was created in the licence (`POST /users`);
 *  - `user_invited`     an invitation was issued to one of its NON-admin users (bulk
 *                       send or resend), never an automatic reminder, which rotates
 *                       the existing invitation;
 *  - `client_logged_in` one of its NON-admin users signed in;
 *  - `list_created`     a list was created for it (created, imported into a new
 *                       list, collected into a new list, or duplicated);
 *  - `list_sent`        one of its lists reached `sent`: the transition that
 *                       stamps `sent_at` (#560);
 *  - `list_approval_requested` an Invyo administrator was asked to approve one of
 *                       its lists, which moved it to `pending_review` (#577).
 *
 * The value is what the database and the API carry. Credits, an admin opening the
 * page and reminders are deliberately left out, and so is every admin account: one
 * is never journaled as added, invited or signed in: the staff-account invite
 * (`POST /invitations/send`) writes nothing at all.
 */

export const LICENCE_ACTIVITY_TYPES = [
    'user_added',
    'user_invited',
    'client_logged_in',
    'list_created',
    'list_sent',
    'list_approval_requested',
] as const;

export type LicenceActivityType = (typeof LICENCE_ACTIVITY_TYPES)[number];

const LICENCE_ACTIVITY_TYPE_SET: ReadonlySet<string> = new Set(LICENCE_ACTIVITY_TYPES);

/** True when `value` is one of `LICENCE_ACTIVITY_TYPES`. */
export function isLicenceActivityType(value: unknown): value is LicenceActivityType {
    return typeof value === 'string' && LICENCE_ACTIVITY_TYPE_SET.has(value);
}
